import { useSuspenseQuery } from '@tanstack/react-query'
import { Outlet, createFileRoute, notFound, useLocation } from '@tanstack/react-router'
import { useCallback, useMemo, useState } from 'react'
import { ScorecardMutators } from '~/mutators/scorecards'
import { useTRPC } from '~/services/trpc'
import { SettingsActions, type TitleSegment, useSettingTitle } from '~/templates/settings'

import {
  Alert,
  AlertDialog,
  AlertTitle,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Tabs,
  TabsList,
  TabsTrigger,
} from '@hap/components'

import { EnableBanner } from './-components/enable-banner'
import { DuplicateScorecardForm } from './-components/scorecard-forms'

export const Route = createFileRoute(
  '/_authenticated/$alias/settings/scorecards/$scorecardId',
)({
  loader: async ({ context, params }) => {
    const scorecard = await context.queryClient.ensureQueryData(
      context.trpc.scorecards.getById.queryOptions(params.scorecardId),
    )

    if (!scorecard) {
      throw notFound()
    }

    return { scorecard }
  },
  component: RouteComponent,
})

function RouteComponent() {
  const trpc = useTRPC()
  const { scorecardId } = Route.useParams()
  const navigate = Route.useNavigate()
  const { data: scorecard } = useSuspenseQuery(
    trpc.scorecards.getById.queryOptions(scorecardId),
  )
  const location = useLocation()
  const activeTab = location.pathname.includes('/criteria') ? 'criteria' : 'settings'

  const { publish, isPending: isPublishPending } = ScorecardMutators.usePublish()

  const { destroy } = ScorecardMutators.useDestroy({
    onSuccess: () => {
      navigate({ to: `/$alias/settings/scorecards` })
    },
  })
  // Title segments with breadcrumb
  const titleSegments: TitleSegment[] = useMemo(
    () =>
      [
        {
          label: 'Scorecards',
          onClick: () => navigate({ to: `/$alias/settings/scorecards` }),
        },
        scorecard.name,
      ] satisfies TitleSegment[],
    [navigate, scorecard.name],
  )

  useSettingTitle(titleSegments)

  const [duplicateVisibility, setDuplicateVisibility] = useState(false)
  const [deleteVisibility, setDeleteVisibility] = useState(false)
  const [scorecardContextMenuVisibility, setScorecardContextMenuVisibility] =
    useState(false)

  const onDelete = useCallback(async () => {
    await destroy({ scorecardId })
  }, [scorecardId, destroy])

  const handlePublishToggle = useCallback(
    (enabled: boolean) => {
      publish({
        scorecardId,
        values: { publish: enabled },
      })
    },
    [publish, scorecardId],
  )

  return (
    <>
      <SettingsActions>
        <DropdownMenu
          open={scorecardContextMenuVisibility}
          onOpenChange={setScorecardContextMenuVisibility}
        >
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" icon="dots-three-outline" size="icon" />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => setDuplicateVisibility(true)} icon="copy">
              Duplicate...
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setDeleteVisibility(true)} icon="trash">
              Delete...
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SettingsActions>

      <DuplicateScorecardForm
        open={duplicateVisibility}
        onOpenChange={setDuplicateVisibility}
        scorecardId={scorecardId}
        scorecardName={scorecard.name}
        onSuccess={(newScorecardId) => {
          navigate({
            to: `/$alias/settings/scorecards/$scorecardId/criteria`,
            params: { scorecardId: newScorecardId },
          })
        }}
      />

      <AlertDialog
        open={deleteVisibility}
        onOpenChange={setDeleteVisibility}
        title="Delete scorecard"
        description="Confirm that you wish to delete this scorecard. This cannot be undone."
        action={{
          onClick: onDelete,
          value: 'Delete',
          variant: 'destructive',
        }}
      />

      <div className="gutters flex h-0 shrink grow flex-col">
        <div className="space-y-6">
          {/* Archived/Deleted Alerts */}
          {!!scorecard.archivedAt && (
            <Alert className="mt-4" variant="warn">
              <AlertTitle>This scorecard has been archived.</AlertTitle>
            </Alert>
          )}
          {!!scorecard.deletedAt && (
            <Alert className="mt-4" variant="warn">
              <AlertTitle>This scorecard has been deleted.</AlertTitle>
            </Alert>
          )}

          {/* Enable Banner */}
          <EnableBanner
            enabled={!!scorecard.publishedAt}
            publishedAt={scorecard.publishedAt}
            onToggle={handlePublishToggle}
            isPending={isPublishPending}
          />

          {/* Tabs */}
          <Tabs
            value={activeTab}
            onValueChange={(value) => {
              navigate({
                to:
                  value === 'settings'
                    ? '/$alias/settings/scorecards/$scorecardId/settings'
                    : '/$alias/settings/scorecards/$scorecardId/criteria',
              })
            }}
          >
            <TabsList>
              <TabsTrigger value="criteria">Criteria</TabsTrigger>
              <TabsTrigger value="settings">Settings</TabsTrigger>
            </TabsList>

            <div className="mt-6">
              <Outlet />
            </div>
          </Tabs>
        </div>
      </div>
    </>
  )
}
